import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShare, faUsers, faSave, faIdCard, faClock, faBook, faClipboardList, faMoneyCheckAlt, 
  faMoneyBillWave, faHandsHelping, faImage, faEye, faPrint, faQrcode, faUser, faLockOpen,
  faSadCry, faWallet, faSearch, faClockRotateLeft, faClipboardQuestion, faCertificate, faToolbox, 
  faQuestionCircle, faBrain
} from '@fortawesome/free-solid-svg-icons';
import './JobsWorks.css';
// import bluehandsimage from '../../assets/gdashboard/BlueHands.png';
import Tooltip from '../../components/utils/Tooltip';

function BlueHandsHome() {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [category, setCategory] = useState("Labour");
  const [searchCity, setSearchCity] = useState("Mumbai");
  const [customCity, setCustomCity] = useState("");
  const [jobsData, setJobsData] = useState([]);
  const [isInstant, setIsInstant] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [applyRemarks, setApplyRemarks] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [shortlistedRemarks, setShortlistedRemarks] = useState("");
  const [shortlisted, setShortlisted] = useState(false);
  const [applied, setApplied] = useState(false);

  const fetchJobs = useCallback(() => {
    const config = {
      url: "/bluejobs/jobs",
      method: "post",
      headers: { Authorization: authState.token },
      data: {
        userId: userId, 
        searchKeyword: searchKeyword, 
        category: category, 
        searchCity: searchCity, 
        customCity: customCity,
        isInstant: isInstant
      },
    };

    fetchData(config, { showSuccessToast: false })
      .then((data) => {
        if (data && data.jobsData) {
          setJobsData(data.jobsData);
        } else {
          console.error("Data not found!!");
          setJobsData([]); 
        }
      })
      .catch((error) => {
        console.error("Error fetching Job data:", error);
        setJobsData([]); 
      });
  }, [authState.token, userId, searchCity, customCity, isInstant, searchKeyword, category, fetchData]);

  useEffect(() => {
    if (authState.isLoggedIn) {
      fetchJobs();
    }
  }, [authState.isLoggedIn, fetchJobs]);

  const handleView = useCallback((jobId) => {
    setSelectedJobId(prevId => (prevId === jobId ? null : jobId));
  }, []);

  const handleJobShare = useCallback((jobId) => {
    setSelectedJobId(jobId);
  }, []);

  const handleJobSave = useCallback((jobId) => {
    setSelectedJobId(jobId);
  }, []);

  const handleJobApply = useCallback(
    (jobcode, jobid, postedby) => {
      const config = {
        url: "/bluejobsapply/apply",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId,
          userCode: authState.user.think5whyscode,
          jobCode: jobcode,
          jobId: jobid,
          postedBy: postedby,
          applyDate: new Date(),
          applyRemarks, 
          isWithdraw: false,
          reviewerId: null,
          reviewerCode: '',
          reviewDate: null,
          reviewRemarks: '',
          reviewStatus: '',
          isHide: false,
        },
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setApplied(true);
        })
        .catch((error) => {
          console.error("Error applying this job:", error);
        });
    },
    [authState.token, authState.user.think5whyscode, userId, fetchData, applyRemarks]
  );

  const saveShortlist = useCallback(
    (jobid, jobcode, jobtitle, employername, postedby, posteddate) => {
      const config = {
        url: "/bluejobsshortlisted/shortlist",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId: userId,
          userCode: userCode,
          jobCode: jobcode,
          jobId: jobid,
          jobTitle: jobtitle,
          jobPostedBy: postedby,
          postedDate: posteddate,
          employerName: employername,
          shortlistedDate: new Date(),
          shortlistedRemarks, 
          isActive: true,
        },
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setShortlisted(true); 
        })
        .catch((error) => {
          setShortlisted(false);
          console.error("Error shortlisting this job:", error);
        });
    },
    [authState.token, userId, userCode, fetchData, shortlistedRemarks]
  );
  
  const saveApply = useCallback(
    (jobid, jobcode, jobtitle, employername, postedby, posteddate) => {
      const config = {
        url: "/bluejobsapply/apply",
        method: "post",
        headers: { Authorization: authState.token },
        data: {
          userId,
          userCode,
          jobCode: jobcode,
          jobTitle: jobtitle,
          jobId: jobid,
          jobPostedBy: postedby,
          postedDate: posteddate,
          employerName: employername,
          applyDate: new Date(),
          applyRemarks, 
          isWithdraw: false,
          reviewerId: null,
          reviewerCode: '',
          reviewDate: null,
          reviewRemarks: '',
          reviewStatus: '',
          isHide: false,
        },
      };
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setApplied(true); 
        })
        .catch((error) => {
          console.error("Error applying this job:", error);
        });
    },
    [authState.token, userId, userCode, fetchData, applyRemarks]
  );

  const formatDateTime = useCallback((date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }, []);

  const formatDate = useCallback((date) => {
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}-${month}-${year}`;
  }, []);

  const JobList = useCallback(({ jobs, category, isInstant, searchKeyword, searchCity, customCity }) => (
    <div className='flex flex-col items-center justify-center w-full'>
      <p className="text-blue-200 text-lg font-medium mb-4">
        {jobs.length} {category} {isInstant ? "Instant" : "Regular"} Jobs Found for {searchKeyword || 'any keywords'} in {searchCity} {customCity || ''}
      </p>
      <div className="w-full space-y-2">
        {jobs.map((job, index) => (
          <JobCard
            key={job._id}
            job={job}
            index={index}
            selectedJobId={selectedJobId}
            onView={handleView}
            onShare={handleJobShare}
            onSave={handleJobSave}
            onApply={handleJobApply}
            shortlisted={shortlisted}
            applied={applied}
            saveShortlist={saveShortlist}
            saveApply={saveApply}
          />
        ))}
      </div>
    </div>
  ), [selectedJobId, handleView, handleJobShare, handleJobSave, handleJobApply, shortlisted, applied, saveShortlist, saveApply]);

  const JobCard = useCallback(({ job, index, selectedJobId, onView, onShare, onSave, onApply, shortlisted, applied, saveShortlist, saveApply }) => (
    <div className="bg-gray-800 border border-gray-700 my-2 p-3 rounded-lg shadow-lg text-sm w-full">
      <div className="flex flex-row justify-between">
        <span className="text-blue-200 font-medium flex-1">
          {index + 1}. {job.jobTitle} ({formatDateTime(job.datePosted)})
        </span>
        <Tooltip text="View details" position="top">
          <button 
            className="ml-4 w-8 text-blue-400 hover:text-blue-300 cursor-pointer p-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={() => onView(job._id)}
          >
            <FontAwesomeIcon icon={faEye} className="text-sm" />
          </button>
        </Tooltip>
      </div>
      {selectedJobId === job._id && (
        <JobDetails 
          job={job} 
          onShare={onShare} 
          onSave={onSave} 
          onApply={onApply}
          shortlisted={shortlisted}
          applied={applied}
          saveShortlist={saveShortlist}
          saveApply={saveApply}
        />
      )}
    </div>
  ), [formatDateTime, selectedJobId]);

  const JobDetails = useCallback(({ job, onShare, onSave, onApply, shortlisted, applied, saveShortlist, saveApply }) => (
    <div className="bg-gray-700 text-blue-100 px-4 py-4 mt-2 rounded-lg border border-gray-600">
      <h2 className="text-blue-200 text-lg font-bold mb-3">Job Details: {job.jobTitle}</h2>
      <div className="space-y-2 text-sm">
        <p><strong className="text-blue-300">Job Code:</strong> {job.jobCode}</p>
        <p><strong className="text-blue-300">Location:</strong> {job.jobLocation}</p>
        <p><strong className="text-blue-300">Category:</strong> {job.jobCategory}</p>
        <p><strong className="text-blue-300">Description:</strong> {job.jobDescription}</p>
        <p><strong className="text-blue-300">Vacancies:</strong> {job.vacancies}</p>
        <p><strong className="text-blue-300">Salary Details:</strong> {job.salaryDetails}</p>
        <p><strong className="text-blue-300">Remarks:</strong> {job.splRemarks}</p>
        <p><strong className="text-blue-300">Employer:</strong> {job.employerName}</p>
        <p>{job.employerDetails}</p>
        <p>{job.employerAddress}</p>
        <p><strong className="text-blue-300">Department:</strong> {job.department}</p>
        <p><strong className="text-blue-300">Application Period:</strong> {formatDate(job.applyFromDate)} to {formatDate(job.applyToDate)}</p>
        <p><strong className="text-blue-300">Date Posted:</strong> {formatDate(job.datePosted)}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <button
          className="bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => saveShortlist(job._id, job.jobCode, job.jobTitle, job.employerName, job.createdBy, job.datePosted)}
          disabled={shortlisted}
        >
          {shortlisted ? 'Shortlisted' : 'Shortlist'}
        </button>
        <button
          className="bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => saveApply(job._id, job.jobCode, job.jobTitle, job.employerName, job.createdBy, job.datePosted)}
          disabled={applied}
        >
          {applied ? 'Applied' : 'Apply'}
        </button>
      </div>
      {shortlisted && (
        <p className="mt-2 text-green-400 text-sm font-medium">You Have Shortlisted The Job!</p>
      )}
      {applied && (
        <p className="mt-2 text-green-400 text-sm font-medium">You Have Successfully Applied For The Job!</p>
      )}
      <div className="flex mt-4 space-x-4">
        <Tooltip text="Share" position="top">
          <button className="text-green-400 hover:text-green-300 cursor-pointer p-1 rounded focus:outline-none focus:ring-2 focus:ring-green-500" onClick={() => onShare(job._id)}>
            <FontAwesomeIcon icon={faShare} className="text-sm" />
          </button>
        </Tooltip>
        <Tooltip text="Save" position="top">
          <button className="text-blue-400 hover:text-blue-300 cursor-pointer p-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => onSave(job._id)}>
            <FontAwesomeIcon icon={faSave} className="text-sm" />
          </button>
        </Tooltip>
        <Tooltip text="Apply" position="top">
          <button className="text-orange-400 hover:text-orange-300 cursor-pointer p-1 rounded focus:outline-none focus:ring-2 focus:ring-orange-500" onClick={() => onApply(job._id)}>
            <FontAwesomeIcon icon={faPrint} className="text-sm" />
          </button>
        </Tooltip>
      </div>
    </div>
  ), [formatDate]);

  const NoJobsMessage = useCallback(({ isInstant, category, searchKeyword, searchCity, customCity }) => (
    <p className="text-blue-300 text-center py-8 font-medium">
      No {isInstant ? "Instant" : "Regular"} {category} Jobs Found for {searchKeyword || 'any keywords'} in {searchCity} {customCity || ''}
    </p>
  ), []);

  return (
    <div>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-8">
        <h2 className="text-2xl font-bold text-blue-200 text-center mb-2 px-4">Workforce Jobboard</h2>
        <div className="min-h-[calc(100vh-8rem)] px-4 max-w-7xl mx-auto">
          <div className="bg-gray-800 border-2 border-blue-700 rounded-lg p-4 sm:p-6 mb-6 shadow-xl">
            <div className="flex flex-col space-y-4">
              {/* Search Input */}
              <div className="flex items-center">
                <FontAwesomeIcon icon={faSearch} className="text-blue-400 text-lg ml-2 h-5 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Enter Keywords eg Electrician, Locality Name etc"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="ml-3 h-10 text-sm bg-gray-700 border border-gray-600 rounded-md px-4 py-2 flex-grow text-blue-200 placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-md px-4 py-2 h-10 text-sm text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a Category</option>
                  <optgroup label="Industrial & Manufacturing">
                    <option value="Factory Worker">Factory Worker</option>
                    <option value="Machine Operator">Machine Operator</option>
                    <option value="Welder">Welder</option>
                  </optgroup>
                  <optgroup label="Construction & Infrastructure">
                    <option value="Mason">Mason</option>
                    <option value="Carpenter">Carpenter</option>
                    <option value="Electrician">Electrician</option>
                    <option value="Plumber">Plumber</option>
                  </optgroup>
                  <optgroup label="Agriculture & Farming">
                    <option value="Farm Labourer">Farm Labourer</option>
                    <option value="Harvesting Worker">Harvesting Worker</option>
                    <option value="Irrigation Technician">Irrigation Technician</option>
                  </optgroup>
                  <optgroup label="Transportation & Logistics">
                    <option value="Truck Driver">Truck Driver</option>
                    <option value="Warehouse Worker">Warehouse Worker</option>
                  </optgroup>
                  <optgroup label="Mining & Extraction">
                    <option value="Coal Miner">Coal Miner</option>
                    <option value="Oil Rig Worker">Oil Rig Worker</option>
                  </optgroup>
                  <optgroup label="Domestic & Cleaning">
                    <option value="Housekeeper">Housekeeper</option>
                    <option value="Janitor">Janitor</option>
                  </optgroup>
                  <optgroup label="Security & Public Safety">
                    <option value="Security Guard">Security Guard</option>
                    <option value="Firefighter">Firefighter</option>
                  </optgroup>
                  <optgroup label="Hospitality & Food Service">
                    <option value="Kitchen Helper">Kitchen Helper</option>
                    <option value="Dishwasher">Dishwasher</option>
                  </optgroup>
                  <optgroup label="Textile & Garment">
                    <option value="Tailor">Tailor</option>
                    <option value="Weaver">Weaver</option>
                  </optgroup>
                  <optgroup label="General & Unskilled Labour">
                    <option value="Construction Helper">Construction Helper</option>
                    <option value="Daily Wage Labourer">Daily Wage Labourer</option>
                  </optgroup>
                  <optgroup label="Other Roles">
                    <option value="Highly Skilled">Highly Skilled</option>
                    <option value="Labour">Labour</option>
                    <option value="Contractor">Contractor</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Company">Company</option>
                    <option value="Supervisor">Supervisor</option>
                  </optgroup>
                </select>
                <select
                  value={searchCity}
                  onChange={(e) => setSearchCity(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-md px-4 py-2 h-10 text-sm text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Mumbai">Mumbai</option>
                  <option value="Delhi">Delhi</option>
                  <option value="Bangalore">Bangalore</option>
                  <option value="Ahmedabad">Ahmedabad</option>
                  <option value="Hyderabad">Hyderabad</option>
                  <option value="Chennai">Chennai</option>
                  <option value="Pune">Pune</option>
                  <option value="">Other Location</option>
                </select>
              </div>

              {/* Custom City Input */}
              {searchCity === "" && (
                <input
                  type="text"
                  placeholder="Enter location"
                  value={customCity}
                  onChange={(e) => setCustomCity(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded-md px-4 py-2 text-sm text-blue-200 placeholder-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}

              {/* Instant Job Checkbox */}
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  className="mr-2 cursor-pointer accent-blue-600 focus:ring-2 focus:ring-blue-500 w-4 h-4 text-blue-600"
                  checked={isInstant} 
                  onChange={(e) => setIsInstant(e.target.checked)} 
                />
                <span className="text-sm font-medium text-blue-200">Get Instant Job</span>
              </div>

              {/* Search Button */}
              <div className="flex justify-center">
                <button
                  onClick={fetchJobs} 
                  className="bg-blue-800 text-white w-full sm:w-40 h-10 flex items-center justify-center rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>

              {/* Jobs List or No Jobs */}
              <div className="w-full">
                {jobsData.length > 0 ? (
                  <JobList
                    jobs={jobsData}
                    category={category}
                    isInstant={isInstant}
                    searchKeyword={searchKeyword}
                    searchCity={searchCity}
                    customCity={customCity}
                  />
                ) : (
                  <NoJobsMessage
                    isInstant={isInstant}
                    category={category}
                    searchKeyword={searchKeyword}
                    searchCity={searchCity}
                    customCity={customCity}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Quick Links Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-8">
            <CardAILink to="/aicounselling-about" icon={faQuestionCircle} text="About AI Counselling" />
            <CardAILink to="/aicounselling-job" icon={faBrain} text="Job AI Counsellor" />
            <CardLink to="/bluehands-shortlisted" icon={faClipboardQuestion} text="My Shortlisted Jobs" />
            <CardLink to="/bluehands-applied" icon={faClockRotateLeft} text="Jobs I Applied" />
            <CardLink to="/bluehands-applied" icon={faEye} text="Who Shortlisted Me" />
            <CardLink to="/bluehands-iamavailable" icon={faHandsHelping} text="I am Available" />
            <CardSkillLink to="/training-list" icon={faToolbox} text="My Skill Trainings" />
            <CardSkillLink to="/my-certificates-others" icon={faCertificate} text="My Skill Certificates" />
            <CardProfileLink to="/myprofile-blueprofile" icon={faUser} text="My Blue Profile" />
            <CardLink to="/contact-genious-myqr" icon={faQrcode} text="My QR Code" />
            <CardLink to="/contact-genious/blue" icon={faIdCard} text="My Secure Blue Card" />
            <CardLink to={`/pub/blue/${userCode}`} icon={faLockOpen} text="My Public Blue Card" />
            <CardLink to="/attendance-list" icon={faClock} text="My Attendance" />
            <CardLink to="/myjournal/2" icon={faBook} text="My Work Diary" />
            <CardLink to="/jobless-list" icon={faSadCry} text="My Joblessness History" />
            <CardLink to="/salary-list" icon={faMoneyBillWave} text="My Salary & Wages" />
            <CardProfileLink to="/bluehands-mybiodata" icon={faUser} text="Write My Bio-Data" />
            <CardProfileLink to="/visualbrief-myvb" icon={faUser} text="My Multimedia Profile" />
            <CardProfileLink to="/visualbrief-myvb" icon={faUser} text="My Work Presentations" />
            <CardProfileLink to="/visualbrief-myvb" icon={faUser} text="My Creativity Presentations" />
            <CardLink to="/thinkaiinterview-mock" icon={faUser} text="ThinkAIInterviewer Mock" />
            <CardNoLink icon={faUser} text="ThinkAIInterviewer Job" />
            <CardAdminLink to="/bluehands-myjobpost-list" icon={faClock} text="Job Postings" />
            <CardAdminLink to="/attendance-capture-qr" icon={faImage} text="Attendance Locations" />
            <CardNoLink icon={faClipboardList} text="Employee Muster Roll" />
            <CardAdminLink to="/bluehands-my" icon={faUsers} text="My BlueHands" />
            <CardLink to="/bluehands-pricelist" icon={faWallet} text="Price List" />
          </div>
        </div>
      </div>
    </div>
  );
}

// CardAILink Component
const CardAILink = ({ to, icon, text }) => (
  <Link 
    to={to} 
    className="bg-blue-900 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 hover:bg-blue-800 flex items-center text-xs sm:text-sm text-blue-200 hover:text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </Link>
);

// CardSkillLink Component
const CardSkillLink = ({ to, icon, text }) => (
  <Link 
    to={to} 
    className="bg-teal-900 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 hover:bg-teal-800 flex items-center text-xs sm:text-sm text-teal-200 hover:text-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-500"
  >
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </Link>
);

// CardAdminLink Component
const CardAdminLink = ({ to, icon, text }) => (
  <Link 
    to={to} 
    className="bg-orange-900 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 hover:bg-orange-800 flex items-center text-xs sm:text-sm text-orange-200 hover:text-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
  >
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </Link>
);

// CardProfileLink Component
const CardProfileLink = ({ to, icon, text }) => (
  <Link 
    to={to} 
    className="bg-blue-800 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 hover:bg-blue-700 flex items-center text-xs sm:text-sm text-blue-200 hover:text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </Link>
);

// CardLink Component
const CardLink = ({ to, icon, text }) => (
  <Link 
    to={to} 
    className="bg-gray-800 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 hover:bg-gray-700 flex items-center text-xs sm:text-sm text-blue-200 hover:text-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-700"
  >
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </Link>
);

// CardNoLink Component
const CardNoLink = ({ icon, text }) => (
  <div className="bg-gray-700 p-3 rounded-lg shadow-lg transition-transform hover:scale-105 cursor-not-allowed opacity-60 flex items-center text-xs sm:text-sm text-gray-400">
    <FontAwesomeIcon icon={icon} className="text-lg mr-3 flex-shrink-0" />
    <span className="flex-1 text-left">{text}</span>
  </div>
);

export default BlueHandsHome;